"use client"
import styled from "@emotion/styled"
import { RentProjectBenefitsItem } from "utils/projects"

import { IconButton } from "@mui/material"
import { useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/effect-fade"
import "swiper/css/free-mode"
import "swiper/css/navigation"
import "swiper/css/thumbs"
import { EffectFade, FreeMode, Navigation, Thumbs } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { NewText } from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { getUmbracoMediaUrl, replaceMediaLinksInRichText } from "utils"

const StyledContainer = styled.div`
  display: flex;
  align-items: stretch;
  overflow: hidden;
  position: relative;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("lg")} {
    height: 444px;
    padding-inline: 14px;
  }
`

const IconSwiperContainer = styled.div`
  width: 70px;
  height: 290px;
  display: flex;
  overflow: hidden;
  position: absolute;
  z-index: 2;
  top: 0;
  inset-inline-start: 0;
  flex-shrink: 0;
  ${(props) => props.theme.breakpoints.up("lg")} {
    position: static;
    width: 90px;
    height: 340px;
    margin-top: 40px;
  }
`
const IconSwiper = styled(Swiper)`
  padding: 0;
`
const ContentSwiper = styled(Swiper)`
  flex-shrink: 0;
  padding: 0;

  ${(props) => props.theme.breakpoints.up("lg")} {
    flex: 1;
  }
`

const StyledItem = styled.article`
  display: flex;
  flex-direction: column;

  ${(props) => props.theme.breakpoints.up("lg")} {
    flex-direction: row;
  }
`
const StyledItemTextContent = styled.div`
  padding: 16px;
  gap: 8px;
  border-radius: 12px;
  border: 1px solid ${(props) => props.theme.colors.darkBlue4rentMarketing};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: var(--benefits-black);
  background-color: white;
  margin-inline-start: 70px;
  margin-block-end: 31px;
  height: 290px;
  ${(props) => props.theme.breakpoints.up("lg")} {
    margin-top: 40px;
    flex-shrink: 0;
    width: 326px;
    height: 340px;
    padding: 24px;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 38px;
  }
`
const StyledItemContent = styled.div`
  margin: 0;
  overflow: auto;
  font-size: 16px;
  font-weight: 300;
  line-height: 145%;
  font-family: var(--narkis);
  display: -webkit-box;
  -webkit-line-clamp: 9;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  p {
    margin-block: 0;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    -webkit-line-clamp: 8;
    font-size: 20px;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: -0.4px;
  }
`

const OverlappingImages = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  aspect-ratio: 335 / 225;
  /* overflow: visible; */

  img {
    width: 110%;
    height: 110%;
    object-fit: cover;
  }
  &::after {
    content: "";
    position: absolute;
    z-index: 3;
    background-image: url("/icons/benefit-connect-icon-mobile.svg");
    background-repeat: no-repeat;
    background-size: contain;
    display: flex;
    width: 32px;
    height: 31px;
    inset-inline-end: calc(32% - 16px);
    inset-block-start: -31px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    aspect-ratio: unset;
    flex: 1;
    aspect-ratio: unset;
    width: 100%;
    height: 444px;
    overflow: visible;
    &::after {
      background-image: url("/icons/benefit-connect-icon-desktop.svg");
      width: 39px;
      height: 31px;

      inset-inline-end: unset;
      inset-block-start: unset;

      inset-inline-start: -39px;
      inset-block-end: calc(28% - 15px);
    }
  }
`

const OverlappingImageSM = styled.div`
  position: absolute;
  inset-block-end: 0;
  inset-inline-start: 0;
  z-index: 2;
  height: 56.445%;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 137 / 127;

  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 284px;
    height: unset;
    aspect-ratio: 284 / 252;
  }
`
const OverlappingImageLG = styled.div`
  border-radius: 12px;
  overflow: hidden;
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  z-index: 1;
  height: 92%;

  aspect-ratio: 223 / 207;
  ${(props) => props.theme.breakpoints.up("lg")} {
    aspect-ratio: 460 / 407;
    width: 460px;
    height: unset;
  }
`
const StyledIconItem = styled(IconButton)<{ isSelected: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  display: flex;
  margin: 0;
  padding: 2px 12px;
  justify-content: center;
  align-items: center;
  border: 1px solid
    ${(props) =>
      props.isSelected ? `${props.theme.colors.blue4rentMarketing} !important` : "#fff"};
  border-radius: 12px;
  background-color: ${(props) =>
    props.isSelected ? `${props.theme.colors.blue4rentMarketing} !important` : "#fff"};

  img {
    width: 32px;
    height: 32px;
    margin: 0;
    padding: 0;
  }
  .normal-icon {
    display: ${(props) => (props.isSelected ? "none" : "flex")};
  }
  .selected-icon {
    display: ${(props) => (props.isSelected ? "flex" : "none")};
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding: 10px 20px;
    img {
      width: 40px;
      height: 40px;
    }
  }
`

const StyledIconSwiperSlide = styled(SwiperSlide)`
  display: flex;
  flex-shrink: 0;
  margin: 0;
  width: max-content;
  height: max-content;
`

type BenefitIconItemProps = {
  iconNormal: string
  iconSelected: string
  title: string
  isSelected: boolean
}

const BenefitIconItem = ({
  iconNormal,
  title,
  iconSelected,
  isSelected,
}: BenefitIconItemProps & { isSelected: boolean }) => {
  const iconSelectedUrl = getUmbracoMediaUrl(iconSelected)
  const iconNormalUrl = getUmbracoMediaUrl(iconNormal)
  return (
    <StyledIconItem isSelected={isSelected}>
      <ImageWithAlt
        className="selected-icon"
        width={40}
        height={40}
        image={{
          url: iconSelectedUrl,
          altText: title,
        }}
      />
      <ImageWithAlt
        className="normal-icon"
        width={40}
        height={40}
        image={{
          url: iconNormalUrl,
          altText: title,
        }}
      />
    </StyledIconItem>
  )
}

type BenefitItemProps = {
  content: string
  largeImage: string
  smallImage: string
  title: string
}
const BenefitItem = ({ content, largeImage, smallImage, title }: BenefitItemProps) => {
  const largeImageUrl = getUmbracoMediaUrl(largeImage)
  const smallImageUrl = getUmbracoMediaUrl(smallImage)
  return (
    <StyledItem>
      <StyledItemTextContent>
        <NewText
          component="h4"
          desktopFontSize={32}
          mobileFontSize={24}
          desktopFontWeight={400}
          desktopLineHeight="125%"
          mobileLineHeight={"125%"}
          sx={{
            margin: 0,
          }}
          color="inherit"
          fontFamily={"var(--narkis)"}
        >
          {title}
        </NewText>
        <StyledItemContent
          dangerouslySetInnerHTML={{ __html: replaceMediaLinksInRichText(content) }}
        />
      </StyledItemTextContent>
      <OverlappingImages>
        <OverlappingImageSM>
          <ImageWithAlt
            fill
            image={{
              url: smallImageUrl,
              altText: title,
            }}
          />
        </OverlappingImageSM>
        <OverlappingImageLG>
          <ImageWithAlt
            fill
            image={{
              url: largeImageUrl,
              altText: title,
            }}
          />
        </OverlappingImageLG>
      </OverlappingImages>
    </StyledItem>
  )
}

type Props = {
  items: Array<RentProjectBenefitsItem>
  locale: string
}
const ProjectBenefitsSlider = ({ items, locale }: Props) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)
  const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0)

  return (
    <StyledContainer>
      <IconSwiperContainer>
        <IconSwiper
          dir={locale === "he" ? "rtl" : "ltr"}
          spaceBetween={10}
          slidesPerView={5}
          onSwiper={setThumbsSwiper}
          direction="vertical"
          modules={[Navigation, Thumbs]}
        >
          {items.map((item, index) => (
            <StyledIconSwiperSlide
              key={index}
              style={{
                width: "100%",
                height: "100%",
              }}
            >
              <BenefitIconItem
                isSelected={activeSlideIndex === index}
                iconNormal={item.iconNormal!}
                iconSelected={item.iconSelected!}
                title={item.title!}
              />
            </StyledIconSwiperSlide>
          ))}
        </IconSwiper>
      </IconSwiperContainer>
      <ContentSwiper
        dir={locale === "he" ? "rtl" : "ltr"}
        thumbs={{ swiper: thumbsSwiper }}
        effect="fade"
        onActiveIndexChange={(swiper) => {
          setActiveSlideIndex(swiper.activeIndex)
        }}
        modules={[FreeMode, EffectFade, Navigation, Thumbs]}
      >
        {items.map((item, index) => (
          <SwiperSlide key={index}>
            <BenefitItem
              content={item.content!}
              largeImage={item.largeImage!}
              smallImage={item.smallImage!}
              title={item.title!}
            />
          </SwiperSlide>
        ))}
      </ContentSwiper>
    </StyledContainer>
  )
}
export default ProjectBenefitsSlider
